<script setup>
import { computed, onMounted } from 'vue'
import { useRoute } from 'vue-router'
import { useProjectStore } from '@/stores/project'
import { useDiscussionStore } from '@/stores/discussion'
import { useMonitoringStore } from '@/stores/monitoring'
import { Database, Activity, Bell, Satellite, MessageCircle } from 'lucide-vue-next'
import { fetchAlerts } from '@/services/monitoringApi'

const tabIcons = {
  ProjectHome: Database,
  ProjectMonitoring: Activity,
  ProjectImagery: Satellite,
  ProjectChat: MessageCircle
}

const props = defineProps({
  tabs: {
    type: Array,
    default: null
  }
})

const route = useRoute()
const projectStore = useProjectStore()
const discussionStore = useDiscussionStore()
const monitoringStore = useMonitoringStore()
const projectId = computed(() => route.params.project_id)
const unreadAlerts = computed(() => monitoringStore.unreadAlerts)

const monitoringEnabled = computed(() => !!projectStore.current?.monitoring_enabled)
const showAlerts = computed(() => !props.tabs && monitoringEnabled.value)

const visibleTabs = computed(() => {
  if (props.tabs) return props.tabs
  const tabs = [{ name: 'ProjectHome', label: 'Data' }]
  if (monitoringEnabled.value) {
    tabs.push(
      { name: 'ProjectMonitoring', label: 'Monitor' },
      { name: 'ProjectImagery', label: 'Imagery' },
    )
  }
  if (discussionStore.isEnabled(projectId.value)) {
    tabs.push({ name: 'ProjectChat', label: 'Chat' })
  }
  return tabs
})

// Populate the badge on tabs that don't load the alert feed themselves;
// the Alerts page overwrites this with its own load/markRead cycle.
onMounted(async () => {
  if (route.name === 'ProjectAlerts') return
  const orgId = route.params.org_id
  if (!orgId || !projectId.value || !projectStore.current?.monitoring_enabled) return
  try {
    const data = await fetchAlerts(orgId, projectId.value)
    monitoringStore.setUnreadAlerts(data.unread || 0)
  } catch (e) {
    // badge stays at its last known value
  }
})
</script>

<template>
  <div
    v-if="visibleTabs.length > 1 || showAlerts"
    class="sticky top-0 z-20 flex items-center gap-2 bg-background px-3 py-2"
  >
    <div v-if="visibleTabs.length > 1" class="flex flex-1 gap-0.5 rounded-lg bg-muted p-0.5">
      <router-link
        v-for="tab in visibleTabs"
        :key="tab.name"
        :to="{ name: tab.name, params: { project_id: projectId } }"
        class="flex flex-1 items-center justify-center gap-1.5 rounded-md px-3 py-1.5 text-center text-sm font-medium transition-colors"
        :class="route.name === tab.name
          ? 'bg-background shadow font-semibold'
          : 'text-muted-foreground hover:text-foreground'"
      >
        <component :is="tabIcons[tab.name]" v-if="tabIcons[tab.name]" class="size-4 shrink-0" />
        {{ tab.label }}
      </router-link>
    </div>
    <div v-if="showAlerts" class="shrink-0 rounded-lg bg-muted p-0.5">
      <router-link
        :to="{ name: 'ProjectAlerts', params: { project_id: projectId } }"
        class="relative flex size-8 items-center justify-center rounded-md transition-colors"
        :class="route.name === 'ProjectAlerts'
          ? 'bg-background shadow text-foreground'
          : 'text-muted-foreground hover:text-foreground'"
        title="Alerts"
        aria-label="Alerts"
      >
        <Bell class="size-4" />
        <span
          v-if="unreadAlerts > 0"
          class="absolute -right-1 -top-1 min-w-4 h-4 px-1 text-[10px] leading-4 rounded-full bg-destructive text-white"
        >
          {{ unreadAlerts > 99 ? '99+' : unreadAlerts }}
        </span>
      </router-link>
    </div>
  </div>
</template>
